import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

// Safe imports for modules (handle both CommonJS & ESM)
import drilldownModule from "highcharts/modules/drilldown";
import accessibilityModule from "highcharts/modules/accessibility";

// Initialize modules safely
if (typeof drilldownModule === "function") {
  drilldownModule(Highcharts);
} else if (typeof drilldownModule.default === "function") {
  drilldownModule.default(Highcharts);
}

if (typeof accessibilityModule === "function") {
  accessibilityModule(Highcharts);
} else if (typeof accessibilityModule.default === "function") {
  accessibilityModule.default(Highcharts);
}

const TicketDashboard = ({ ticketdata }) => {
  const users = ticketdata.users;
  const ageGroups = ticketdata.ageGroups;
  const userTicketAgingData = ticketdata.userTicketAgingData;
  const modules = ticketdata.modules;
  const moduleAgingData = ticketdata.moduleAgingData;

  const userDrilldownSeries = users.map((user) => {
    const groupData = Object.keys(ticketdata.userDrillDown[user] || {}).map(
      (group) => {
        const tickets = ticketdata.userDrillDown[user][group];
        return {
          name: group,
          y: tickets.length,
          tickets: tickets,
        };
      }
    );
    return {
      id: user,
      name: `Ticket aging for ${user}`,
      data: groupData,
    };
  });
  const moduleDrilldownSeries = modules.map((mod) => {
    const groupData = ageGroups.map((group) => {
      // Get actual ticket IDs from moduleDrillDown
      const tickets = ticketdata.moduleDrillDown[mod]?.[group] || [];

      return {
        name: group, // x-axis label
        y: tickets.length, // number of tickets
        tickets: tickets, // actual ticket IDs for tooltip
      };
    });

    return {
      id: mod, // drilldown id = module name
      name: `Tickets in ${mod}`,
      data: groupData,
    };
  });

  // === USER AGING CHART ===
  const userAgingOptions = {
    chart: { type: "column", backgroundColor: "transparent" },
    title: { text: "Active Ticket by User", align: "center" },
    xAxis: { type: "category", crosshair: true }, // 👈 type: "category" important
    yAxis: { min: 0, title: { text: "Number of Tickets" } },
    tooltip: {
      formatter: function () {
        if (this.point.tickets) {
          return `<b>${this.key}</b><br/>Count: ${
            this.y
          }<br/>Tickets: ${this.point.tickets.join(", ")}`;
        }
        return `<b>${this.series.name}</b>: ${this.y}`;
      },
    },
    plotOptions: { column: { stacking: "normal", cursor: "pointer" } },
    series: ageGroups.map((group) => ({
      name: group,
      data: users.map((user, i) => ({
        name: user, // 👈 important: name per point
        y: userTicketAgingData[group][i],
        drilldown: user,
      })),
    })),
    drilldown: { series: userDrilldownSeries },
  };

  const moduleAgingOptions = {
    chart: { type: "column", backgroundColor: "transparent" },
    title: { text: "Active Ticket by Module", align: "center" },
    xAxis: { type: "category" }, // allows names on x-axis
    yAxis: { min: 0, title: { text: "Number of Tickets" } },
    tooltip: {
      formatter: function () {
        if (this.point.tickets) {
          return `<b>${this.key}</b><br/>Count: ${
            this.y
          }<br/>Tickets: ${this.point.tickets.join(", ")}`;
        }
        return `<b>${this.series.name}</b>: ${this.y}`;
      },
    },
    plotOptions: {
      series: { stacking: "normal", borderWidth: 0 }, // remove global cursor
    },
    series: ageGroups.map((group) => ({
      name: group,
      data: modules.map((mod, i) => ({
        name: mod,
        y: moduleAgingData[group][i],
        drilldown: mod, // drilldown by module
        cursor: "pointer", // only drillable points
      })),
    })),
    drilldown: { series: moduleDrilldownSeries },
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* User Ticket Aging */}
        <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
          <HighchartsReact highcharts={Highcharts} options={userAgingOptions} />
        </div>

        {/* Module Ticket Aging */}
        <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
          <HighchartsReact
            highcharts={Highcharts}
            options={moduleAgingOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default TicketDashboard;
